import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAppStore } from '@/stores/appStore';

const API_BASE = import.meta.env.VITE_API_BASE_URL ?? '';
const TOKEN_KEY = 'enflux_token';
// Id of the user the persisted app store (agency/workspace selection) belongs to.
const LAST_USER_KEY = 'enflux_last_user_id';
// Backoff between /api/auth/me retries on transient failures (network, 5xx, 429).
const ME_RETRY_DELAYS_MS = [1000, 2000, 4000];

export interface AuthUser {
  id: string;
  email: string;
  name: string | null;
  firstName?: string | null;
  emailAddresses: Array<{ emailAddress: string }>;
  fullName?: string | null;
  isSuperadmin: boolean;
}

interface AuthContextValue {
  user: AuthUser | null;
  token: string | null;
  isLoaded: boolean;
  isSignedIn: boolean;
  /** A stored token couldn't be checked (network/server trouble, not a 401). */
  authError: boolean;
  retryAuth: () => void;
  getToken: () => Promise<string | null>;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string, name?: string) => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

function shapeUser(u: {
  id: string;
  email: string;
  name: string | null;
  is_superadmin?: number | boolean;
}): AuthUser {
  const firstName = u.name?.split(' ')[0] ?? null;
  return {
    id: u.id,
    email: u.email,
    name: u.name,
    firstName,
    fullName: u.name,
    emailAddresses: [{ emailAddress: u.email }],
    isSuperadmin: u.is_superadmin === 1 || u.is_superadmin === true,
  };
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const queryClient = useQueryClient();
  const [token, setToken] = useState<string | null>(() => localStorage.getItem(TOKEN_KEY));
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [authError, setAuthError] = useState(false);
  const [retryNonce, setRetryNonce] = useState(0);

  // Drop everything scoped to the previous user: the persisted agency/workspace
  // selection and every cached query. Otherwise the next person on this browser
  // inherits them and gets 403s across the app.
  const resetUserState = useCallback(() => {
    useAppStore.setState({ currentAgency: null, currentWorkspace: null });
    queryClient.clear();
  }, [queryClient]);

  // Called whenever a user is established; resets state left by a different one.
  // A missing marker only resets on an explicit sign-in/sign-up: sessions that
  // predate the marker keep their agency/workspace selection on the first /me.
  const adoptUser = useCallback((id: string, resetIfUnknown: boolean) => {
    const prev = localStorage.getItem(LAST_USER_KEY);
    if (prev !== id) {
      if (prev !== null || resetIfUnknown) resetUserState();
      localStorage.setItem(LAST_USER_KEY, id);
    }
  }, [resetUserState]);

  useEffect(() => {
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const wait = (ms: number) => new Promise<void>((resolve) => { timer = setTimeout(resolve, ms); });

    async function loadMe() {
      if (!token) {
        setUser(null);
        setAuthError(false);
        setIsLoaded(true);
        return;
      }
      for (let attempt = 0; ; attempt++) {
        let status = 0;
        try {
          const resp = await fetch(`${API_BASE}/api/auth/me`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          status = resp.status;
          if (resp.ok) {
            const data = await resp.json() as { user: { id: string; email: string; name: string | null; is_superadmin?: number } };
            if (cancelled) return;
            adoptUser(data.user.id, false);
            setUser(shapeUser(data.user));
            setAuthError(false);
            setIsLoaded(true);
            return;
          }
        } catch {
          // Network failure, or the request was aborted (e.g. the page is
          // reloading) — transient, so the token is kept.
        }
        if (cancelled) return;
        // Only a definitive rejection of the token signs the user out. A 5xx,
        // 429 or network blip must not throw away a still-valid session.
        if (status === 401) {
          localStorage.removeItem(TOKEN_KEY);
          setToken(null);
          setUser(null);
          setAuthError(false);
          setIsLoaded(true);
          return;
        }
        if (attempt >= ME_RETRY_DELAYS_MS.length) {
          setAuthError(true);
          setIsLoaded(true);
          return;
        }
        await wait(ME_RETRY_DELAYS_MS[attempt]);
        if (cancelled) return;
      }
    }
    loadMe();
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [token, retryNonce, adoptUser]);

  const retryAuth = useCallback(() => {
    setAuthError(false);
    setIsLoaded(false);
    setRetryNonce((n) => n + 1);
  }, []);

  // Try again as soon as the browser reports connectivity is back.
  useEffect(() => {
    if (!authError) return;
    window.addEventListener('online', retryAuth);
    return () => window.removeEventListener('online', retryAuth);
  }, [authError, retryAuth]);

  const signIn = useCallback(async (email: string, password: string) => {
    const resp = await fetch(`${API_BASE}/api/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });
    const data = await resp.json() as { token?: string; user?: { id: string; email: string; name: string | null; is_superadmin?: number }; error?: string };
    if (!resp.ok || !data.token || !data.user) {
      throw new Error(data.error ?? 'Sign in failed');
    }
    adoptUser(data.user.id, true);
    localStorage.setItem(TOKEN_KEY, data.token);
    setToken(data.token);
    setUser(shapeUser(data.user));
  }, [adoptUser]);

  const signUp = useCallback(async (email: string, password: string, name?: string) => {
    const resp = await fetch(`${API_BASE}/api/auth/signup`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password, name }),
    });
    const data = await resp.json() as { token?: string; user?: { id: string; email: string; name: string | null; is_superadmin?: number }; error?: string };
    if (!resp.ok || !data.token || !data.user) {
      throw new Error(data.error ?? 'Sign up failed');
    }
    adoptUser(data.user.id, true);
    localStorage.setItem(TOKEN_KEY, data.token);
    setToken(data.token);
    setUser(shapeUser(data.user));
  }, [adoptUser]);

  const signOut = useCallback(async () => {
    if (token) {
      await fetch(`${API_BASE}/api/auth/logout`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      }).catch(() => {});
    }
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(LAST_USER_KEY);
    resetUserState();
    setToken(null);
    setUser(null);
  }, [token, resetUserState]);

  const getToken = useCallback(async () => token, [token]);

  const value: AuthContextValue = {
    user,
    token,
    isLoaded,
    isSignedIn: !!user,
    authError,
    retryAuth,
    getToken,
    signIn,
    signUp,
    signOut,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
}

export function useUser() {
  const { user, isLoaded } = useAuth();
  return { user, isLoaded };
}
